// @flow
import { ApiClient, EvaluationApi, EvaluationBatchRequest } from 'flagr';
import { find, propEq, curry } from 'ramda';
import type { User } from './auth';
import { reduce } from '../helpers/utils';
import config from '../../config/config';

const debug = require('debug')('jp:stores:features');

const LOAD = 'features/LOAD';
const LOAD_SUCCESS = 'features/LOAD_SUCCESS';
const LOAD_FAIL = 'features/LOAD_FAIL';

type Flag = {| key: string, name: string |};
type Flags = Array<Flag>;

export type Feature = {|
  key: string,
  enabled: boolean,
  segmentID?: ?number,
  variantID?: ?number,
  variantKey?: ?string,
  variantAttachment?: ?Object
|};

type RawFeature = {|
  flagID: number,
  flagKey: string,
  segmentID: ?number,
  variantID: ?number,
  variantKey: ?string,
  variantAttachment: Object,
  evalContext: {
    entityType: string,
    flagID: number,
    entityID: string,
    entityContext: Object
  },
  timestamp: string,
  flagSnapshotID: number
|};

export type State = {|
  loaded: boolean,
  loading: boolean,
  features: Array<Feature>
|};

const FLAGS: Flags = [
  { key: 'kshb778sw8c5i16gc', name: 'hotjar_enabled' },
  {
    key: 'disable_jp_web_route_edit_listing',
    name: 'disable_jp_web_route_edit_listing'
  },
  {
    key: 'driveup_checkout',
    name: 'driveup_checkout'
  },
  {
    key: 'disable_jp_web_route_dashboard_vehicles',
    name: 'disable_jp_web_route_dashboard_vehicles'
  },
  {
    key: 'disable_jp_web_route_dashboard_payments',
    name: 'disable_jp_web_route_dashboard_payments'
  },
  {
    key: 'disable_jp_web_route_dashboard_withdrawal',
    name: 'disable_jp_web_route_dashboard_withdrawal'
  },
  {
    key: 'jp_web_disable_walking_times_display',
    name: 'jp_web_disable_walking_times_display'
  },
  {
    key: 'jp_web_disable_old_location_details',
    name: 'jp_web_disable_old_location_details'
  },
  {
    key: 'primary_header_invest_link',
    name: 'primary_header_invest_link'
  },
  {
    key: 'disable_jp_web_route_dashboard_availability',
    name: 'disable_jp_web_route_dashboard_availability'
  },
  {
    key: 'disable_jp_web_route_dashboard_parking_spaces',
    name: 'disable_jp_web_route_dashboard_parking_spaces'
  },
  {
    key: 'home_banner',
    name: 'home_banner'
  },
  {
    key: 'google_recaptcha_use_v2',
    name: 'google_recaptcha_use_v2'
  }
];

const flagKeys = FLAGS.map((flag: Flag) => flag.key);

const findBy = curry((propName: string, propValue: any) =>
  find(propEq(propName, propValue))
);
const findFlagByKey = findBy('key');
const findFeatureByKey = findBy('key');

const isRawFeatureEnabled = (feature: RawFeature): boolean =>
  feature.segmentID !== undefined &&
  feature.variantID !== undefined &&
  feature.variantKey !== undefined;

const initialState: State = {
  loaded: false,
  loading: false,
  features: []
};

type GlobalState = { features: State };

type Action =
  | {| type: typeof LOAD |}
  | {| type: typeof LOAD_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_FAIL, payload: Object |}
  | {|
      types: Array<string>,
      params?: Object,
      promise: (?ApiClient) => Promise<{}>
    |};

export default reduce(initialState, {
  [LOAD](state: State): State {
    debug(LOAD);
    return { ...state, loading: true };
  },
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, action);
    if (!action.payload && !action.payload.evaluationResults) {
      return state;
    }

    const features = action.payload.evaluationResults
      .map(
        (feature: RawFeature): ?Feature => {
          if (!config.isProduction) {
            debug(feature.flagID, feature.evalDebugLog);
          }
          const flag: Flag = findFlagByKey(feature.flagKey)(FLAGS);
          if (!flag) {
            return false;
          }
          return {
            key: flag.name,
            enabled: isRawFeatureEnabled(feature),
            variantKey: feature.variantKey,
            variantID: feature.variantID,
            variantAttachment: feature.variantAttachment,
            segmentID: feature.segmentID
          };
        }
      )
      .filter(elem => typeof elem === 'object');

    return {
      ...state,
      loading: false,
      loaded: true,
      features: [...state.features, ...features]
    };
  },
  [LOAD_FAIL](state: State, action): State {
    debug(LOAD_FAIL, action);
    return { ...state, loading: false, loaded: false };
  }
});

export function isFeatureEnabled(
  features: State,
  featureName: string,
  variant?: string
): boolean {
  const isEnabled = (() => {
    const feature = findFeatureByKey(featureName)(features.features);

    if (!feature) {
      return false;
    }

    let { enabled } = feature;
    if (variant) {
      enabled = enabled && feature.variantKey === variant;
    }
    return enabled;
  })();

  debug('isFeatureEnabled?', featureName, isEnabled);
  return isEnabled;
}

export function getFeatureFromState(
  features: State,
  featureName: string
): ?Feature {
  return findFeatureByKey(featureName)(features.features);
}

const buildBatchRequest = (identifier, userLoggedIn, user, fingerprint) => {
  debug(`Using identifier: ${identifier}`);

  const evalRequest = new EvaluationBatchRequest([
    {
      entityId: identifier,
      entityType: 'user',
      entityContext: {
        userLoggedIn: Boolean(userLoggedIn).toString(),
        identifier,
        ...user,
        useragent: fingerprint.components.useragent,
        geoip: fingerprint.components.geoip
      }
    }
  ]);
  evalRequest.flagKeys = flagKeys;
  if (!config.isProduction) {
    evalRequest.enableDebug = true;
  }
  return evalRequest;
};

export function load(user: ?User, fingerprint: Object, entityId: string) {
  ApiClient.instance.basePath = config.featureApiEndpoint;

  const apiInstance = new EvaluationApi();
  let userLoggedIn = false;
  const identifier = entityId;
  if (user) {
    userLoggedIn = !!user.id;
  }
  return (dispatch: Action => void, getState: () => GlobalState) => {
    if (!getState().features.loading && !getState().features.loaded) {
      return dispatch({
        types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
        promise: () =>
          new Promise((resolve, reject) => {
            apiInstance.postEvaluationBatch(
              buildBatchRequest(identifier, userLoggedIn, user, fingerprint),
              (error, data) => {
                if (error) {
                  reject(new Error('Error getting features from FEATURE API'));
                } else {
                  resolve({ ...data });
                }
              }
            );
          })
      });
    }
    return Promise.resolve(getState().features.features);
  };
}
